/**
 * AgentsFlowAutomations
 * Tela "Automações" de um AgentFlow: lista lembretes configurados no grafo
 * + listagem paginada de mensagens agendadas, com filtro por telefone,
 * deleção individual e em massa.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconArrowLeft,
  IconBellRinging,
  IconRefresh,
  IconAlertTriangle,
  IconSearch,
  IconTrash,
  IconClock,
  IconMail,
  IconBrandWhatsapp,
  IconCalendarEvent,
  IconCheckbox,
  IconSquare,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';

import AgentsFlowService from '../services/AgentsFlowService';

import '../styles/agents-flow-screen.css';
import './agents-flow-automations.css';

const PAGE_SIZE = 20;

const TRIGGER_LABEL = {
  before_event: 'Antes do agendamento',
  after_creation: 'Após a criação',
  after_event: 'Após o evento',
};

const STATUS_LABEL = {
  pending: 'Pendente',
  skipped: 'Ignorado',
  sent: 'Enviado',
  canceled: 'Cancelado',
  failed: 'Falhou',
};

const STATUS_TONE = {
  pending: 'afa-status--pending',
  skipped: 'afa-status--skipped',
  sent: 'afa-status--sent',
  canceled: 'afa-status--canceled',
  failed: 'afa-status--failed',
};

function ChannelIcon({ channel }) {
  if (channel === 'whatsapp') return <IconBrandWhatsapp size={16} />;
  if (channel === 'email') return <IconMail size={16} />;
  return <IconBellRinging size={16} />;
}

function formatOffset(item) {
  if (item.trigger === 'before_event' && item.minutes_before) {
    return `${item.minutes_before} min antes`;
  }
  if (item.trigger === 'after_creation' && item.minutes_after) {
    return `${item.minutes_after} min após criação`;
  }
  if (item.trigger === 'after_event' && item.minutes_after_event) {
    return `${item.minutes_after_event} min após o evento`;
  }
  return '—';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function AutomationCard({ automation }) {
  return (
    <div className="afa-automation-card">
      <div className="afa-automation-card__header">
        <div className="afa-automation-card__channel">
          <ChannelIcon channel={automation.channel} />
        </div>
        <div className="afa-automation-card__body">
          <div className="afa-automation-card__title">
            {automation.node_label || 'Lembrete'}
            <span className="afa-automation-card__badge">
              {TRIGGER_LABEL[automation.trigger] || automation.trigger}
            </span>
          </div>
          <div className="afa-automation-card__meta">
            <span><IconClock size={14} /> {formatOffset(automation)}</span>
            {automation.channel && (
              <span className="afa-automation-card__channel-label">
                {automation.channel === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
              </span>
            )}
          </div>
          {automation.message && (
            <p className="afa-automation-card__message">{automation.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AgentsFlowAutomations() {
  const { id: agentId } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [page, setPage] = useState(1);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [phoneFilterDraft, setPhoneFilterDraft] = useState('');
  const [pendingOnly, setPendingOnly] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const safeSet = useCallback((setter, value) => {
    if (isMountedRef.current) setter(value);
  }, []);

  const loadAgent = useCallback(async () => {
    try {
      const response = await AgentsFlowService.getAgent(agentId);
      safeSet(setAgent, response.data);
    } catch (err) {
      console.error('Erro ao carregar agente:', err);
      const status = err?.response?.status;
      const msg = status === 404
        ? 'Agente não encontrado.'
        : (err?.response?.data?.error || 'Erro ao carregar agente.');
      safeSet(setError, msg);
    }
  }, [agentId, safeSet]);

  const loadAutomations = useCallback(async () => {
    try {
      const response = await AgentsFlowService.getAutomations(agentId);
      safeSet(setAutomations, response.data?.results || []);
      safeSet(setPendingCount, response.data?.pending_messages_count || 0);
    } catch (err) {
      console.error('Erro ao carregar automações:', err);
      safeSet(setError, err?.response?.data?.error || 'Erro ao carregar automações.');
    }
  }, [agentId, safeSet]);

  const loadMessages = useCallback(async () => {
    safeSet(setLoadingMessages, true);
    try {
      const params = {
        agent_flow_id: agentId,
        page,
        page_size: PAGE_SIZE,
        ordering: '-send_date',
      };
      if (phoneFilter) params.number_contains = phoneFilter;
      if (pendingOnly) params.pending_only = 'true';

      const response = await AgentsFlowService.listScheduledMessages(params);
      safeSet(setMessages, response.data?.results || []);
      safeSet(setTotalMessages, response.data?.count || 0);
    } catch (err) {
      console.error('Erro ao carregar mensagens agendadas:', err);
      safeSet(setError, err?.response?.data?.error || 'Erro ao carregar mensagens.');
    } finally {
      safeSet(setLoadingMessages, false);
    }
  }, [agentId, page, phoneFilter, pendingOnly, safeSet]);

  useEffect(() => {
    safeSet(setLoading, true);
    Promise.all([loadAgent(), loadAutomations()])
      .finally(() => safeSet(setLoading, false));
  }, [loadAgent, loadAutomations, safeSet]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalMessages / PAGE_SIZE)), [totalMessages]);

  const visibleSelectableIds = useMemo(() => (
    messages
      .filter((m) => ['pending', 'skipped'].includes(m.status))
      .map((m) => m.id)
  ), [messages]);

  const allVisibleSelected = visibleSelectableIds.length > 0
    && visibleSelectableIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleSelectableIds.forEach((id) => next.delete(id));
      } else {
        visibleSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allVisibleSelected, visibleSelectableIds]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleApplyFilter = useCallback((e) => {
    e?.preventDefault?.();
    setPage(1);
    setPhoneFilter(phoneFilterDraft.trim());
  }, [phoneFilterDraft]);

  const handleClearFilter = useCallback(() => {
    setPhoneFilterDraft('');
    setPhoneFilter('');
    setPage(1);
  }, []);

  const handleTogglePendingOnly = useCallback(() => {
    setPendingOnly((prev) => !prev);
    setPage(1);
  }, []);

  const handleDeleteOne = useCallback(async (message) => {
    const targetLabel = message.contact_name || message.number || 'este contato';
    const result = await Swal.fire({
      title: 'Excluir mensagem agendada?',
      text: `Tem certeza que deseja excluir o agendamento para ${targetLabel}? Esta ação não pode ser desfeita.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await AgentsFlowService.deleteScheduledMessage(message.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(message.id);
        return next;
      });
      await Promise.all([loadMessages(), loadAutomations()]);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao excluir',
        text: err?.response?.data?.error || 'Não foi possível excluir.',
      });
    }
  }, [loadMessages, loadAutomations]);

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const result = await Swal.fire({
      title: `Excluir ${ids.length} mensagem(ns) agendada(s)?`,
      text: 'Esta ação não pode ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      const response = await AgentsFlowService.bulkDeleteScheduledMessages(ids);
      const deleted = response.data?.deleted_count ?? ids.length;
      setSelectedIds(new Set());
      await Promise.all([loadMessages(), loadAutomations()]);
      Swal.fire({
        icon: 'success',
        title: 'Mensagens excluídas',
        text: `${deleted} mensagem(ns) excluída(s) com sucesso.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao excluir em massa',
        text: err?.response?.data?.error || 'Não foi possível excluir as mensagens.',
      });
    }
  }, [selectedIds, loadMessages, loadAutomations]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([loadAutomations(), loadMessages()]);
  }, [loadAutomations, loadMessages]);

  return (
    <Container fluid>
      <div className="afs-header">
        <div className="afs-header__left">
          <OverlayTrigger placement="bottom" overlay={<Tooltip>Voltar para agentes</Tooltip>}>
            <button
              type="button"
              className="afa-back-btn"
              onClick={() => navigate('/agents-flow')}
            >
              <IconArrowLeft size={20} />
            </button>
          </OverlayTrigger>
          <div className="afs-header__icon afa-header-icon">
            <IconBellRinging size={22} />
          </div>
          <div>
            <h1 className="afs-header__title">Automações</h1>
            <p className="afs-header__subtitle">
              {agent ? `Agente: ${agent.name}` : 'Carregando agente...'}
            </p>
          </div>
        </div>
        <div className="afs-header__actions">
          <button type="button" className="afs-btn-secondary" onClick={handleRefresh}>
            <IconRefresh size={16} />
            Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="afa-error">
          <IconAlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      <section className="afa-section">
        <h2 className="afa-section__title">Automações ativas do agente</h2>
        <p className="afa-section__subtitle">
          Lembretes configurados no grafo deste agente. Configure-os no editor visual.
        </p>

        {loading ? (
          <div className="afa-loading">Carregando...</div>
        ) : automations.length === 0 ? (
          <div className="afa-empty">
            <IconBellRinging size={28} />
            <h3>Sem automações configuradas</h3>
            <p>Edite o agente e adicione lembretes no nó de agendamento para vê-los aqui.</p>
          </div>
        ) : (
          <div className="afa-automations-grid">
            {automations.map((a) => (
              <AutomationCard key={`${a.node_id}-${a.index}`} automation={a} />
            ))}
          </div>
        )}
      </section>

      <section className="afa-section">
        <div className="afa-section__header">
          <div>
            <h2 className="afa-section__title">Mensagens agendadas</h2>
            <p className="afa-section__subtitle">
              {pendingCount} pendente(s) no total para este agente.
            </p>
          </div>
        </div>

        <form className="afa-toolbar" onSubmit={handleApplyFilter}>
          <div className="afa-filter">
            <IconSearch size={16} className="afa-filter__icon" />
            <input
              type="text"
              className="afa-filter__input"
              placeholder="Buscar por telefone..."
              value={phoneFilterDraft}
              onChange={(e) => setPhoneFilterDraft(e.target.value)}
            />
            {phoneFilter && (
              <button
                type="button"
                className="afa-filter__clear"
                onClick={handleClearFilter}
              >
                Limpar
              </button>
            )}
            <button type="submit" className="afs-btn-primary afa-filter__submit">
              Filtrar
            </button>
          </div>

          <label className="afa-checkbox">
            <input
              type="checkbox"
              checked={pendingOnly}
              onChange={handleTogglePendingOnly}
            />
            <span>Somente pendentes</span>
          </label>

          {selectedIds.size > 0 && (
            <button
              type="button"
              className="afa-bulk-delete"
              onClick={handleBulkDelete}
            >
              <IconTrash size={16} />
              Excluir {selectedIds.size}
            </button>
          )}
        </form>

        {loadingMessages ? (
          <div className="afa-loading">Carregando...</div>
        ) : messages.length === 0 ? (
          <div className="afa-empty">
            <IconCalendarEvent size={28} />
            <p>Nenhuma mensagem agendada para este agente.</p>
          </div>
        ) : (
          <div className="afa-table-wrapper">
            <table className="afa-table">
              <thead>
                <tr>
                  <th className="afa-table__select">
                    <button
                      type="button"
                      className="afa-checkbox-btn"
                      onClick={toggleSelectAll}
                      disabled={visibleSelectableIds.length === 0}
                      aria-label="Selecionar tudo"
                    >
                      {allVisibleSelected
                        ? <IconCheckbox size={18} />
                        : <IconSquare size={18} />}
                    </button>
                  </th>
                  <th>Contato</th>
                  <th>Telefone</th>
                  <th>Canal</th>
                  <th>Envio</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((m) => {
                  const isSelectable = ['pending', 'skipped'].includes(m.status);
                  const isSelected = selectedIds.has(m.id);
                  return (
                    <tr key={m.id} className={isSelected ? 'afa-row--selected' : ''}>
                      <td>
                        <button
                          type="button"
                          className="afa-checkbox-btn"
                          onClick={() => toggleOne(m.id)}
                          disabled={!isSelectable}
                          aria-label={isSelected ? 'Desselecionar' : 'Selecionar'}
                        >
                          {isSelected
                            ? <IconCheckbox size={18} />
                            : <IconSquare size={18} />}
                        </button>
                      </td>
                      <td>{m.contact_name || '—'}</td>
                      <td>{m.number || '—'}</td>
                      <td>
                        <span className="afa-channel">
                          <ChannelIcon channel={m.channel} />
                          {m.channel === 'whatsapp' ? 'WhatsApp'
                            : m.channel === 'email' ? 'E-mail'
                            : '—'}
                        </span>
                      </td>
                      <td>{formatDate(m.send_date)}</td>
                      <td>
                        <span className={`afa-status ${STATUS_TONE[m.status] || ''}`}>
                          {STATUS_LABEL[m.status] || m.status || '—'}
                        </span>
                      </td>
                      <td>
                        {isSelectable && (
                          <OverlayTrigger placement="top" overlay={<Tooltip>Excluir</Tooltip>}>
                            <button
                              type="button"
                              className="afa-action-btn afa-action-btn--danger"
                              onClick={() => handleDeleteOne(m)}
                            >
                              <IconTrash size={16} />
                            </button>
                          </OverlayTrigger>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalMessages > 0 && (
          <div className="afa-pagination">
            <span>
              Página {page} de {totalPages} ({totalMessages} mensagem{totalMessages !== 1 ? 's' : ''})
            </span>
            <div className="afa-pagination__actions">
              <button
                type="button"
                className="afs-btn-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loadingMessages}
              >
                Anterior
              </button>
              <button
                type="button"
                className="afs-btn-secondary"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loadingMessages}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </section>
    </Container>
  );
}
